import { PropsWithChildren, ReactNode } from 'react';

import { FormControlLabel } from '@mui/material';
import { useField } from 'react-final-form';

import {
  Divider,
  InfoTooltip,
  Paper,
  Skeleton,
  ExternalLink,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { advancedEvictorConfigPlaceholder } from 'features/cluster/autoscaler/_constants/advancedEvictorConfiguration';
import { notReachable } from 'utils/types/not-reachable';

import { AggressiveModeSettings } from './AggressiveModeSettings';
import {
  FIELD_NAMES,
  MAX_SAVINGS_THRESHOLD_PERCENT,
  MIN_SAVINGS_THRESHOLD_PERCENT,
  YAML_INDENTATION,
} from '../../_constants';
import {
  AvailableKentContinuousRebalancingConfigMode,
  FormValues,
} from '../../_types';
import { Section } from '../Section';

import {
  RffCheckbox,
  RffInput,
  RffSlider,
  RffTimeInput,
  RffYamlEditor,
} from '@cast/shared-rff-ui';

import { getDocsLink } from '@cast/utils';

type SettingProps = {
  title: ReactNode;
  description: ReactNode;
  children: ReactNode;
};

const Setting = ({ title, description, children }: SettingProps) => {
  return (
    <Stack direction="row" gap={196}>
      <Stack gap={4} width={360}>
        <Typography variant="P12B">{title}</Typography>
        <Typography variant="P12R" color="textSecondary">
          {description}
        </Typography>
      </Stack>
      <Box width={484}>{children}</Box>
    </Stack>
  );
};

const Layout = ({
  children,
  testId,
}: PropsWithChildren<{ testId?: string }>) => {
  return (
    <Section title="Additional settings" testId={testId}>
      <Paper
        sx={{
          p: 24,
          minWidth: 'max-content',
          minHeight: '190px',
          width: '100%',
        }}
      >
        <Stack gap={24} height="100%" divider={<Divider />}>
          {children}
        </Stack>
      </Paper>
    </Section>
  );
};

const CodeLabel = ({ children }: PropsWithChildren) => (
  <Typography
    variant="C14"
    bgcolor={tokens.theme.bg.structural.code}
    color={tokens.theme.fg.structural.primaryOnInverse}
    component="span"
    borderRadius="2px"
    px={4}
    display="inline-flex"
    alignItems="center"
    height={16}
  >
    {children}
  </Typography>
);

const shouldShowAdvancedConfiguration = (mode: FormValues['mode']) => {
  switch (mode) {
    case 'CONTINUOUS_REBALANCING_CYCLE':
    case 'BIN_PACKING':
      return true;
    case 'EMPTY_NODE_REMOVAL':
      return false;
    default:
      return notReachable(mode);
  }
};

type CycleIntervalInputTitleProps = {
  children: ReactNode;
  title: string;
  infoTooltipTitle: string;
};

const CycleIntervalInputTitle = ({
  children,
  title,
  infoTooltipTitle,
}: CycleIntervalInputTitleProps) => {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" gap={4} alignItems="center">
        <Typography variant="P12M">{title}</Typography>
        <InfoTooltip
          iconSx={{ color: tokens.theme.fg.structural.secondary }}
          title={infoTooltipTitle}
        />
      </Stack>
      {children}
    </Stack>
  );
};

const CycleIntervalSettings = ({
  mode,
}: {
  mode: AvailableKentContinuousRebalancingConfigMode;
}) => {
  const fullModeCycleIntervalSecondsInput = (
    <CycleIntervalInputTitle
      title="Rebalancing"
      infoTooltipTitle="Interval between full rebalancing cycles (node replacement, bin-packing and empty node removal)"
    >
      <RffTimeInput
        testId="consolidation-full-mode-cycle-interval-input"
        timeUnit="second"
        allowedTimeUnits={['second', 'hour', 'minute']}
        name={FIELD_NAMES.fullModeCycleIntervalSeconds}
      />
    </CycleIntervalInputTitle>
  );

  const drainOnlyCycleIntervalSecondsInput = (
    <CycleIntervalInputTitle
      title="Bin-packing"
      infoTooltipTitle="Interval between binpacking of existing nodes and drain-only cycles."
    >
      <RffTimeInput
        testId="consolidation-drain-only-cycle-interval-input"
        timeUnit="second"
        allowedTimeUnits={['second', 'hour', 'minute']}
        name={FIELD_NAMES.drainOnlyCycleIntervalSeconds}
      />
    </CycleIntervalInputTitle>
  );

  const deleteEmptyCycleIntervalSecondsInput = (
    <CycleIntervalInputTitle
      title="Empty node removal"
      infoTooltipTitle="Interval between empty node removal cycles."
    >
      <RffTimeInput
        testId="consolidation-delete-empty-cycle-interval-input"
        timeUnit="second"
        allowedTimeUnits={['second', 'hour', 'minute']}
        name={FIELD_NAMES.deleteEmptyCycleIntervalSeconds}
      />
    </CycleIntervalInputTitle>
  );

  return (
    <Stack gap={16}>
      {(() => {
        switch (mode) {
          case 'CONTINUOUS_REBALANCING_CYCLE':
            return (
              <>
                {fullModeCycleIntervalSecondsInput}
                {drainOnlyCycleIntervalSecondsInput}
                {deleteEmptyCycleIntervalSecondsInput}
              </>
            );
          case 'BIN_PACKING':
            return (
              <>
                {drainOnlyCycleIntervalSecondsInput}
                {deleteEmptyCycleIntervalSecondsInput}
              </>
            );
          case 'EMPTY_NODE_REMOVAL':
            return deleteEmptyCycleIntervalSecondsInput;
          default:
            return notReachable(mode);
        }
      })()}
    </Stack>
  );
};

export const AdditionalSettingsSection = () => {
  const enabledField = useField<FormValues['enabled']>(FIELD_NAMES.enabled);
  const modeField = useField<FormValues['mode']>(FIELD_NAMES.mode);
  const savingsThresholdEnabledField = useField<
    FormValues['savingsThresholdEnabled']
  >(FIELD_NAMES.savingsThresholdEnabled);

  const enabled = enabledField.input.value;
  const mode = modeField.input.value;

  const shouldHideAdditionalSettings = !enabled || !mode;

  if (shouldHideAdditionalSettings) {
    return null;
  }

  return (
    <Layout testId="consolidation-additional-settings">
      <Setting
        title="TTL setting"
        description="Set the amount of time an empty node should remain in the cluster."
      >
        <RffTimeInput
          testId="consolidation-ttl-input"
          label="Keep empty node alive for:"
          timeUnit="second"
          allowedTimeUnits={['second', 'hour', 'minute']}
          name={FIELD_NAMES.minNodeAgeSeconds}
        />
      </Setting>
      <Setting
        title="Cycle intervals"
        description="Set how often each consolidation operation runs. Shorter intervals react faster; longer intervals reduce churn."
      >
        <CycleIntervalSettings mode={mode} />
      </Setting>
      <Setting
        title="Aggressive mode"
        description="Binpack and rebalance problematic pods, those without a controller, job pods, and pods with the removal-disabled annotation."
      >
        <AggressiveModeSettings />
      </Setting>
      {mode === 'CONTINUOUS_REBALANCING_CYCLE' && (
        <Setting
          title="Savings threshold"
          description="The operation will only be executed if the set thresholds can be met."
        >
          <Stack gap={12}>
            <FormControlLabel
              sx={{ gap: '8px', ml: 0 }}
              control={
                <RffCheckbox
                  testId="consolidation-savings-threshold-checkbox"
                  name={FIELD_NAMES.savingsThresholdEnabled}
                  size="small"
                />
              }
              label={
                <Typography variant="P12M">Set savings threshold</Typography>
              }
            />

            {savingsThresholdEnabledField.input.value && (
              <Stack direction="row" gap={16} alignItems="center" ml={24}>
                <RffInput
                  testId="consolidation-savings-threshold-input"
                  name={FIELD_NAMES.savingsThresholdPercent}
                  type="number"
                  min={MIN_SAVINGS_THRESHOLD_PERCENT}
                  max={MAX_SAVINGS_THRESHOLD_PERCENT}
                  endAdornment="%"
                  sx={{
                    width: 64,
                  }}
                />

                <RffSlider
                  name={FIELD_NAMES.savingsThresholdPercent}
                  sx={{ width: 'auto', flexGrow: 1 }}
                  min={MIN_SAVINGS_THRESHOLD_PERCENT}
                  max={MAX_SAVINGS_THRESHOLD_PERCENT}
                />
              </Stack>
            )}
          </Stack>
        </Setting>
      )}
      {shouldShowAdvancedConfiguration(mode) && (
        <Setting
          title="Advanced configuration"
          description={
            <Typography variant="P12R" color="textSecondary" mb={8}>
              Use <CodeLabel>podSelector</CodeLabel> and{' '}
              <CodeLabel>nodeSelector</CodeLabel> to evict or protect specific
              resources.
              <ExternalLink
                sx={{ ml: 2 }}
                link={getDocsLink('docs_evictor_advanced_configuration')}
              >
                Documentation
              </ExternalLink>
            </Typography>
          }
        >
          <RffYamlEditor
            testId="consolidation-advanced-configuration"
            name={FIELD_NAMES.advancedConfiguration}
            indentation={YAML_INDENTATION}
            placeholder={advancedEvictorConfigPlaceholder}
            sx={{
              '& .CodeEditor-editor': {
                minHeight: 380,
              },
            }}
          />
        </Setting>
      )}
    </Layout>
  );
};

const AdditionalSettingsSectionSkeleton = () => {
  return (
    <Layout>
      <Skeleton height="100%" width="100%" />
    </Layout>
  );
};

AdditionalSettingsSection.Skeleton = AdditionalSettingsSectionSkeleton;
